// Fallback only — the live interval comes from data-interval (autoplay_interval setting).
const AUTOADVANCE_INTERVAL = 3000;

export default (node) => {
  const items = Array.from(node.querySelectorAll('[data-category-index]'));
  const images = Array.from(node.querySelectorAll('[data-category-image]'));
  const dots = Array.from(node.querySelectorAll('[data-dot]'));
  const seeCollection = node.querySelector('[data-see-collection]');
  const prevBtn = node.querySelector('[data-category-prev]');
  const nextBtn = node.querySelector('[data-category-next]');
  const liveRegion = node.querySelector('[data-category-live]');

  const count = images.length;
  if (count === 0) return;

  // A single category still needs its title click and CTA wired — only the
  // cycling (auto-advance, arrows, dots) has nothing to move between. Liquid
  // omits the arrows and dots at this count; see category-overview.liquid.
  const canCycle = count > 1;

  const interval = Number(node.dataset.interval) || AUTOADVANCE_INTERVAL;
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  let current = 0;
  let timer = null;
  let started = false;
  let observer = null;

  const trackNavigate = (method, category) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'category_overview_navigate',
      method,
      category: category || '',
    });
  };

  const markActive = (el, active) => {
    el.classList.toggle('is-active', active);
    if (active) {
      el.setAttribute('aria-current', 'true');
    } else {
      el.removeAttribute('aria-current');
    }
  };

  const setActive = (index, method) => {
    const i = ((index % count) + count) % count;
    if (i === current && method !== 'init') return;
    current = i;

    items.forEach((el, idx) => markActive(el, idx === i));
    images.forEach((el, idx) => el.classList.toggle('is-active', idx === i));
    dots.forEach((el, idx) => markActive(el, idx === i));

    // Announce the newly active category to assistive tech (WCAG 4.1.3) — not on initial render
    if (liveRegion && method && method !== 'init') {
      const title = items[i] && items[i].dataset.categoryTitle;
      if (title) liveRegion.textContent = title;
    }

    if (seeCollection) {
      const url = items[i] && items[i].dataset.collectionUrl;
      if (url) {
        seeCollection.href = url;
        seeCollection.classList.remove('is-disabled');
        seeCollection.removeAttribute('aria-disabled');
        seeCollection.removeAttribute('tabindex');
      } else {
        // No collection link for this category — make the dead link look and
        // behave disabled (mouse + keyboard) instead of a clickable dead-end.
        seeCollection.removeAttribute('href');
        seeCollection.classList.add('is-disabled');
        seeCollection.setAttribute('aria-disabled', 'true');
        seeCollection.setAttribute('tabindex', '-1');
      }
    }

    if (method && method !== 'init') {
      trackNavigate(method, items[i] && items[i].dataset.categoryTitle);
    }
  };

  const stopAutoAdvance = () => {
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
  };

  const startAutoAdvance = () => {
    stopAutoAdvance();
    if (prefersReducedMotion.matches) return;
    timer = setInterval(() => {
      if (!node.isConnected) {
        stopAutoAdvance();
        return;
      }
      setActive(current + 1, 'auto');
    }, interval);
  };

  if (canCycle) {
    // Start auto-cycling once the module scrolls into view (both breakpoints), so
    // an off-screen module never burns a timer cycling images nobody can see.
    observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || started) return;
        started = true;
        startAutoAdvance();
        observer.disconnect();
        observer = null;
      },
      { threshold: 0.25 }
    );
    observer.observe(node);

    // Pause on interaction, no resume (confirmed UX decision).
    // Desktop: hovering the module pauses; hovering a name switches.
    node.addEventListener('mouseenter', stopAutoAdvance);
    // Keyboard parity: focus entering the module also pauses auto-advance (WCAG 2.1.1 / 2.2.2)
    node.addEventListener('focusin', stopAutoAdvance);
  }

  // Hover-capable (desktop): a click on the title navigates to its collection.
  // Touch: a tap switches the active category (no navigation).
  const canHover = window.matchMedia('(hover: hover)');

  // Index by array position so the component stays correct even when some
  // category blocks are skipped (image-less blocks aren't rendered).
  items.forEach((el, idx) => {
    el.addEventListener('mouseenter', () => setActive(idx, 'hover'));
    el.addEventListener('click', (e) => {
      const url = el.dataset.collectionUrl;
      // A tap normally switches category rather than navigating — but with a
      // single category there is nothing to switch to, so it would be a dead
      // control. Navigate instead, matching the desktop behaviour.
      if (url && (canHover.matches || !canCycle)) {
        window.location.href = url;
        return;
      }
      e.preventDefault();
      stopAutoAdvance();
      setActive(idx, 'tap');
    });
  });

  dots.forEach((el, idx) => {
    el.addEventListener('click', () => {
      stopAutoAdvance();
      setActive(idx, 'dot');
    });
  });

  if (prevBtn) {
    prevBtn.addEventListener('click', () => {
      stopAutoAdvance();
      setActive(current - 1, 'arrow');
    });
  }

  if (nextBtn) {
    nextBtn.addEventListener('click', () => {
      stopAutoAdvance();
      setActive(current + 1, 'arrow');
    });
  }

  // Sync the "See collection" href with the initial active category.
  setActive(0, 'init');
};
